import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { AuctionDownloadedService } from '@core/services/auction-downloaded.service';
import { AuthService } from '@core/services/auth.service';
import { ModalUtilsService } from '@core/services/modal-utils.service';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import {
  faBell,
  faEnvelope,
  faHeart,
  faUser,
} from '@fortawesome/free-regular-svg-icons';
import {
  faCartPlus,
  faChevronDown,
  faSearch,
  faShoppingBag,
  faSignOutAlt,
  faSlidersH,
} from '@fortawesome/free-solid-svg-icons';
import {
  NgbCollapseModule,
  NgbDropdownModule,
} from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe } from '@ngx-translate/core';
import { ILanguage } from '@shared/interfaces/language.interface';
import { Catalog, FilterStatus } from '@shared/interfaces/search.interface';
import { LanguageService } from '@shared/services/language.service';
import { MessageService } from '@shared/services/message.service';
import { PlatformService } from '@shared/services/platform-service.service';
import { RedirectService } from '@shared/services/redirect.service';
import { SearchService } from '@shared/services/search.service';
import { NotificationService } from 'src/app/account/services/notification.service';
import { ContactsDialogComponent } from 'src/app/article/components/dialogs/contacts-dialog/contacts-dialog.component';
import { AccountDialogComponent } from 'src/app/login/components/account-dialog/account-dialog.component';
import { ActivateAccountPopupComponent } from 'src/app/login/pages/activate-account-popup/activate-account-popup.component';
import { LoginService } from 'src/app/login/services/login.service';
import { PagesService } from 'src/app/pages-manager/services/pages.service';
import { SellerBrandingService } from 'src/app/seller/services/seller-branding.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-header-mobile',
  templateUrl: './header-mobile.component.html',
  styleUrls: ['./header-mobile.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FaIconComponent,
    NgbDropdownModule,
    TranslatePipe,
    FormsModule,
    NgbCollapseModule,
  ],
})
export class HeaderMobileComponent implements OnInit {
  faHeart = faHeart;
  faBell = faBell;
  faEnvelope = faEnvelope;
  faUser = faUser;
  faSearch = faSearch;
  faSlidersH = faSlidersH;
  faChevronDown = faChevronDown;
  faSignOutAlt = faSignOutAlt;
  faCartPlus = faCartPlus;
  faShoppingBag = faShoppingBag;

  isProd = !environment.production;

  imgCountry = signal('/assets/img/it.png');

  @ViewChild('searchInputHeader')
  searchInputHeader: ElementRef<HTMLInputElement>;
  @ViewChild('accountMobileDropdown') accountMobileDropdown;
  @ViewChild('searchInput') searchInput: ElementRef;

  private readonly auctionDownloadedService = inject(AuctionDownloadedService);
  private readonly loginService = inject(LoginService);
  public readonly authService = inject(AuthService);
  public readonly languageService = inject(LanguageService);
  public readonly searchService = inject(SearchService);
  private readonly modalUtils = inject(ModalUtilsService);
  private readonly router = inject(Router);
  private readonly redirectService = inject(RedirectService);
  public readonly notificationService = inject(NotificationService);
  public readonly messageService = inject(MessageService);
  private readonly platformService = inject(PlatformService);
  private readonly branding = inject(SellerBrandingService);
  private readonly pagesService = inject(PagesService);

  // Computed: view is branded only if branding active and current url contains '/seller/'
  isBrandedView = computed(() => {
    return this.branding.isBranded();
  });

  showSearchBar = signal(false);

  isMenuCollapsed = signal(true);
  searchText = signal('');
  hideSearch = input(true);

  isShopPage = signal(false);

  links = signal([
    {
      label: 'IDS_HOW_WORK',
      route: '/how-works-memorabid',
      class: '',
    },
    {
      label: 'IDS_BECOME_SELLER',
      route: '/become-seller',
      class: 'become-seller-link',
    },
    {
      label: 'IDS_BLOG',
      route: '/blog',
      class: '',
    },
    {
      label: 'IDS_HOW_WORK_AUCTIONS',
      route: '/how-works-auctions',
      class: '',
    },
    {
      label: 'IDS_DIRECT_BIDDING',
      route: '/direct-bidding',
      class: '',
    },
    {
      label: 'IDS_WHO_WE_ARE',
      route: '/about-us',
      class: '',
    },
  ] as { label: string; route: string; class: string }[]);

  // Dynamic header pages (seller-aware)
  headerPages = signal<{ label: string; route: string }[]>([]);
  fullPathHeaderPages = computed(() =>
    this.headerPages().map((link) => ({
      label: link.label,
      route: '/' + this.languageService.userSelectedLanguage.code + link.route,
    }))
  );

  homePath = computed(() => {
    if (this.isBrandedView()) {
      const brandUrl = this.branding.getBrandHomeUrl();
      if (brandUrl) {
        return `/${this.languageService.userSelectedLanguage.code}/${brandUrl}`;
      }
    }
    return `/${this.languageService.userSelectedLanguage.code}/home`;
  });

  // Default (non-branded) header pages
  private static readonly DEFAULT_HEADER_PAGES: {
    label: string;
    route: string;
  }[] = [
    { label: 'IDS_ASTE', route: '/search-result-all' },
    { label: 'IDS_OTHER_CATEGORIES', route: '/prestige' },
    { label: 'IDS_SHOP', route: '/shop' },
  ];

  constructor() {
    effect(
      () => {
        this.router.events.subscribe((event) => {
          if (event instanceof NavigationEnd) {
            this.isShopPage.set(event.url.includes('shop'));
          }
          this.isMenuCollapsed.set(true);
        });
      },
      { allowSignalWrites: true }
    );

    this.languageService.selectedLanguage$.subscribe((lang) => {
      this.setFlag(lang.code);
    });

    // React to branding activation changes; wait until branding decision resolved
    effect(
      () => {
        if (!this.branding.brandingReady()) {
          // Show nothing yet (or keep existing) until decision is made
          return;
        }
        if (this.branding.isBranded()) {
          this.loadHeaderPages();
        } else {
          this.headerPages.set([...HeaderMobileComponent.DEFAULT_HEADER_PAGES]);
        }
      },
      { allowSignalWrites: true }
    );
  }

  ngOnInit(): void {
    // Handle back button press on mobile
    this.platformService.runOnBrowser(() => {
      window.addEventListener('popstate', () => this.handleBackButton());
    });
  }

  @HostListener('document:click', ['$event'])
  clickout(event: Event) {
    const target = event.target as HTMLElement | null;
    if (!target?.closest('.navbar-collapse')) {
      this.isMenuCollapsed.set(true);
    }
  }

  handleBackButton() {
    if (this.showSearchBar()) {
      this.showSearchBar.set(false);
      history.pushState(null, '', location.href);
    }
  }

  setFlag(lang: string) {
    this.imgCountry.set('/assets/img/' + lang + '.png');
  }

  search() {
    this.auctionDownloadedService.scrollPosition = 0;
    this.showSearchBar.set(false);

    const auctionStatus = FilterStatus.All;
    const searchRoute = 'search-result';

    this.searchService.setFilters(searchRoute, {
      idLanguage: this.languageService.userSelectedLanguage.idLanguage,
      searchText: this.searchText(),
      idAuctionType: -1,
      idDetail: null,
      onlySigned: false,
      idSport: null,
      idCompetition: null,
      idTeam: null,
      idSeason: null,
      onlyNew: false,
      onlyExpiring: false,
      onlyArriving: false,
      onlyBuyNow: false,
      auctionStatus: auctionStatus,
      idEntity: null,
      idCampaign: null,
      idPromoter: null,
      idActiveAuction: null,
      idTagList: [],
      catalog: Catalog.All,
      orderBy: 'evidence',
      orderDirection: 'desc',
    });

    this.router.navigate(
      ['/', this.languageService.userSelectedLanguage.code, searchRoute],
      {
        queryParamsHandling: 'merge',
        replaceUrl: true,
      }
    );
    setTimeout(() => {
      this.searchService.updateQueryParams(this.searchService.filters);
    }, 500);
  }

  onIconPressed(
    nextPageRoute: string,
    _queryParams: {
      [k: string]: string | number | boolean | null | undefined;
    } = {}
  ) {
    if (this.authService.isAuthenticated()) {
      if (
        nextPageRoute === 'account' &&
        this.authService.userLogged.idProfile === 1
      ) {
        nextPageRoute = 'admin';
      }
      if (nextPageRoute === 'admin-v2') {
        window.open(environment.adminPath, '_blank');
        return;
      }
      this.redirectService.redirect(nextPageRoute);
    } else {
      if (nextPageRoute === 'account/messages') {
        const modal = this.modalUtils.openModal(ContactsDialogComponent, {
          size: 'lg',
          keyboard: false,
        });
        modal.componentInstance.onClickIconMessageHeader = '/about-us';
      } else {
        if (this.authService.authResult === -2) {
          this.modalUtils.openModal(ActivateAccountPopupComponent, {
            size: 'md',
            keyboard: false,
          });
        } else {
          const ref = this.modalUtils.openModal(AccountDialogComponent, {
            size: 'md',
            keyboard: false,
          });
          ref.componentInstance.nextPageRoute = nextPageRoute;
        }
      }
    }
  }

  onAccountPressed(nextPageRoute: string) {
    if (this.authService.isAuthenticated()) {
      if (
        nextPageRoute === 'account' &&
        this.authService.userLogged.idProfile === 1
      ) {
        nextPageRoute = 'admin';
      }
    } else {
      this.accountMobileDropdown.close();
      if (this.authService.authResult === -2) {
        this.modalUtils.openModal(ActivateAccountPopupComponent, {
          size: 'md',
          keyboard: false,
        });
      } else {
        const ref = this.modalUtils.openModal(AccountDialogComponent, {
          size: 'md',
          keyboard: false,
        });
        ref.componentInstance.nextPageRoute = nextPageRoute;
      }
    }
  }

  logout() {
    if (this.authService.isAuthenticated() || this.authService.isToActivate()) {
      this.loginService.logout().subscribe(() => {
        this.authService.logout();
        this.notificationService.unreadNotificationsCount = 0;
        this.messageService.unreadMessagesCount = 0;
        this.messageService.clearRefreshTimer();
        this.redirectService.redirect('home');
      });
    }
  }

  changeLanguages(lang: ILanguage) {
    this.languageService.changeLanguage(lang);
  }

  trackRoute(item: { route: string }) {
    return item.route;
  }

  toggleSearchBar() {
    this.showSearchBar.set(!this.showSearchBar());
    if (this.showSearchBar()) {
      setTimeout(() => this.searchInput.nativeElement.focus(), 0);
      document.body.classList.add('search-active');
      history.pushState(null, '', location.href);
    } else {
      document.body.classList.remove('search-active');
    }
  }

  private loadHeaderPages() {
    const activeSeller = this.branding.activeSeller();
    const idSeller = activeSeller?.idEntity;
    if (!idSeller) {
      return;
    }
    this.pagesService.getHeaderPages(idSeller).subscribe((pages) => {
      this.headerPages.set([
        {
          label: 'IDS_SELLER_HOME',
          route: `/seller/${idSeller}`,
        },

        ...pages.map((p) => ({
          label: p.pageName,
          route: '/' + (p.pageRoute || ''),
        })),
      ]);
    });
  }

  // Accessibility noop handler referenced in template
  noop(_e: KeyboardEvent) {}

  linkTrack(_i: number, item: { route: string }) {
    return item.route;
  }
}
